// Shared Postman-collection parser + lookup helpers.
//
// Extracted from ApiDocumentation.jsx so the API-docs endpoint navigator (rendered in the
// Console's left column, see components/console/ApiDocsNav.jsx) and the docs detail page
// parse collections identically — endpoint `id`s are deterministic, so both sides agree
// and endpoints are deep-linkable via ?e=<id>.

// Parse a Postman v2 collection into a nested [{name, folders[], endpoints[]}] tree.
export function parsePostmanCollection(collection) {
  if (!collection || !collection.item) return [];

  const collections = [];
  const collectionMap = {};

  const processItem = (item, parentPath = [], parentCollection = null) => {
    if (item.item) {
      // It's a folder/collection
      const folderName = item.name || "Default";
      const currentPath = [...parentPath, folderName];
      const pathKey = currentPath.join("/");

      let folderCollection;
      if (!collectionMap[pathKey]) {
        folderCollection = {
          id: `${pathKey}-${Date.now()}-${Math.random()}`,
          name: folderName,
          path: currentPath,
          pathKey,
          endpoints: [],
          folders: [],
          level: parentPath.length,
        };
        collectionMap[pathKey] = folderCollection;

        // Add to parent's folders if parent exists
        if (parentCollection) {
          parentCollection.folders.push(folderCollection);
        } else {
          // Top-level collection
          collections.push(folderCollection);
        }
      } else {
        folderCollection = collectionMap[pathKey];
      }

      // Process sub-items recursively
      item.item.forEach((subItem) =>
        processItem(subItem, currentPath, folderCollection)
      );
    } else if (item.request) {
      // It's an endpoint
      const request = item.request;
      const url = request.url;
      let path = "";

      if (url?.raw) {
        // Extract path from raw URL (remove domain)
        try {
          const urlObj = new URL(url.raw);
          path = urlObj.pathname;
        } catch {
          // If URL parsing fails, try to extract path manually
          const match = url.raw.match(/https?:\/\/[^/]+(\/.*)/);
          if (match) {
            path = match[1];
          } else {
            // Fallback: use path array
            path =
              "/" +
              (Array.isArray(url.path) ? url.path.join("/") : url.path || "");
          }
        }
      } else if (url?.path) {
        path =
          "/" +
          (Array.isArray(url.path) ? url.path.join("/") : url.path || "");
      }

      // Extract path variables
      const pathParams = [];
      if (url?.variable) {
        url.variable.forEach((v) => {
          pathParams.push({
            key: v.key,
            type: "string",
            required: false,
            description: v.description || "",
          });
          // Replace in path
          path = path.replace(`{{${v.key}}}`, `:${v.key}`);
        });
      }

      const endpoint = {
        id: `${parentPath.join("-")}-${item.name}`
          .toLowerCase()
          .replace(/\s+/g, "-"),
        name: item.name,
        method: request.method || "GET",
        path: path,
        description: item.request?.description || "",
        headers: (request.header || []).map((h) => ({
          key: h.key,
          value: h.value || "",
          required: false,
        })),
        body: [],
        params: pathParams,
      };

      // Parse body
      if (request.body) {
        if (request.body.mode === "raw" && request.body.raw) {
          try {
            const bodyJson = JSON.parse(request.body.raw);
            Object.keys(bodyJson).forEach((key) => {
              endpoint.body.push({
                key,
                type: Array.isArray(bodyJson[key])
                  ? "array"
                  : typeof bodyJson[key],
                required: false,
                description: "",
                example: bodyJson[key],
              });
            });
          } catch {
            // Not JSON, try to extract fields from string
            const rawBody = request.body.raw;
            const jsonMatch = rawBody.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              try {
                const bodyJson = JSON.parse(jsonMatch[0]);
                Object.keys(bodyJson).forEach((key) => {
                  endpoint.body.push({
                    key,
                    type: Array.isArray(bodyJson[key])
                      ? "array"
                      : typeof bodyJson[key],
                    required: false,
                    description: "",
                    example: bodyJson[key],
                  });
                });
              } catch {
                // Still can't parse
              }
            }
          }
        } else if (request.body.formdata) {
          request.body.formdata.forEach((f) => {
            endpoint.body.push({
              key: f.key,
              type: f.type || "string",
              required: false,
              description: f.description || "",
            });
          });
        } else if (request.body.urlencoded) {
          request.body.urlencoded.forEach((f) => {
            endpoint.body.push({
              key: f.key,
              type: f.type || "string",
              required: false,
              description: f.description || "",
            });
          });
        }
      }

      // Add endpoint to the most recent parent collection (last in path)
      if (parentPath.length > 0) {
        const parentKey = parentPath.join("/");
        if (collectionMap[parentKey]) {
          collectionMap[parentKey].endpoints.push(endpoint);
        } else {
          // This shouldn't happen if parsing is correct, but handle it
          console.warn(`Parent collection not found for path: ${parentKey}`);
          // Create parent collection if it doesn't exist
          const parentName = parentPath[parentPath.length - 1];
          const newCollection = {
            id: `${parentKey}-${Date.now()}-${Math.random()}`,
            name: parentName,
            path: parentPath,
            pathKey: parentKey,
            endpoints: [endpoint],
            folders: [],
            level: parentPath.length - 1,
          };
          collectionMap[parentKey] = newCollection;

          // Add to appropriate parent or top level
          if (parentPath.length > 1) {
            const grandParentKey = parentPath.slice(0, -1).join("/");
            if (collectionMap[grandParentKey]) {
              collectionMap[grandParentKey].folders.push(newCollection);
            } else {
              collections.push(newCollection);
            }
          } else {
            collections.push(newCollection);
          }
        }
      } else {
        // No parent, create default collection
        const defaultCollection = {
          id: `default-${Date.now()}-${Math.random()}`,
          name: "Default",
          path: [],
          pathKey: "Default",
          endpoints: [endpoint],
          folders: [],
          level: 0,
        };
        collections.push(defaultCollection);
        collectionMap["Default"] = defaultCollection;
      }
    }
  };

  if (Array.isArray(collection.item)) {
    collection.item.forEach((item) => processItem(item));
  } else {
    processItem(collection.item);
  }

  return collections;
}

// Depth-first walk yielding every endpoint across all folders.
export function flattenEndpoints(collections) {
  const out = [];
  const walk = (nodes) => {
    (nodes || []).forEach((c) => {
      (c.endpoints || []).forEach((e) => out.push(e));
      if (c.folders?.length) walk(c.folders);
    });
  };
  walk(collections);
  return out;
}

// Find one endpoint by its deterministic id.
export function findEndpointById(collections, id) {
  if (!id) return null;
  return flattenEndpoints(collections).find((e) => e.id === id) || null;
}
